"use client"

import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'
import ThreeCanvas from '@/widgets/three-canvas/ui/three-canvas'
import { KioskToastContainer } from './kiosk-toast-container'
import { useKioskAvatarPage } from '../model/use-kiosk-avatar-page'
import { KioskCoverModal } from './kiosk-cover-modal'
import { KioskIndicator } from './kiosk-indicator'
import { KioskIdleTimer } from './kiosk-idle-timer'
import { KioskHeader } from './kiosk-header'
import { KioskLanguageDropdown } from './kiosk-language-selector'
import { KioskChatHistoryModal } from './kiosk-chat-history-modal'
import { KioskChatHistoryButton } from './kiosk-chat-history-button'
import { useRealtimeAgentStore } from '@/shared/store/realtime-agent'
import { useToastStore } from '@/layouts/custom-avatar-page/toast/model/toast-store'
import { changeLanguage } from '@/shared/lib/i18n'
import { LipsyncTuningPanel } from '@/features/avatar/lipsync-tuning/ui/lipsync-tuning-panel'
import type { KioskAvatarPageProps } from '../types/kiosk-config'
import { CANCEL_TEXT, KIOSK_LANGUAGES, END_CHAT_BUTTON_TEXT, AI_DISCLAIMER_TEXT } from '../types/kiosk-config'

// 아바타별 프로필 이미지 매핑
const AVATAR_PROFILE_IMAGES: Record<string, string> = {
  'nalnani': '/webp/nani-profile.webp',
  'nani': '/webp/nani-profile.webp',
  'kikiz': '/webp/kikiz-profile.webp',
  'glyx': '/webp/glyx.webp',
  'golgolz': '/webp/golgolz.webp',
  'bambam': '/webp/nani-profile.webp', // fallback
  'vroid': '/webp/vroid.webp',
  'gangwha': '/webp/nani-profile.webp', // fallback
  'bobbi': '/webp/nani-profile.webp', // fallback
  'taekwondo': '/webp/nani-profile.webp', // fallback
  'goesan': '/webp/nani-profile.webp', // fallback
  'custom': '/webp/nani-profile.webp', // fallback
}

const getAvatarProfileImage = (avatarType: string): string => {
  return AVATAR_PROFILE_IMAGES[avatarType] || '/webp/nani-profile.webp'
}

/**
 * 키오스크 모드 전용 아바타 페이지
 *
 * 웹 모드(CustomAvatarPage)와 완전히 다른 UI:
 * - 음성 전용 (텍스트 입력 없음)
 * - 자동 재연결
 * - 30초 유휴 타이머 (20초 무음 → 10초 카운트다운)
 * - Figma 디자인 기반 헤더 (로고 + 언어 선택)
 * - Figma 디자인 기반 인디케이터
 *
 * 로딩 UI 통합:
 * - KioskCoverModal이 로딩 UI 역할을 겸함
 * - 블로킹 로딩 화면 없이 즉시 KioskCoverModal 표시
 * - 통합 로딩 상태로 일관된 UX 제공
 */
export const KioskAvatarPage = ({ agentId }: KioskAvatarPageProps) => {
  const {
    customAvatarPageProps,
    indicatorState,
    isTimerVisible,
    remainingSeconds,
    selectedLanguage,
    setSelectedLanguage,
    showCoverModal,
    unifiedLoadingState,
    loadingMessage,
    pitch,
    handleStart,
    handleScreenTouch,
    resetIdleTimer,
    handleLogoReset,
  } = useKioskAvatarPage({ agentId })

  const {
    avatarType,
    customAvatar,
    agentData,
    isCustomAvatarLoaded,
    mergedConfig,
  } = customAvatarPageProps

  // 배경 스타일 계산 (키오스크 전용 배경)
  const backgroundStyle = useMemo(() => {
    const overlay = mergedConfig?.chatOverlay

    // 키오스크 전용 배경 (에이전트 설정에 kioskBackground가 있으면 사용)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const kioskBg = (overlay as any)?.kioskBackground as string | undefined
    if (kioskBg) {
      return { background: kioskBg } as React.CSSProperties
    }

    // Figma 기본 배경 (node-id=2302-1573, fill_7XID5I)
    return {
      background: [
        'linear-gradient(0deg, rgba(149, 58, 50, 0) 87%, rgba(150, 76, 121, 0.25) 96%, rgba(150, 76, 121, 0.4) 100%)',
        'linear-gradient(180deg, rgba(149, 58, 50, 0) 40%, rgba(150, 76, 121, 0.5) 66%, rgba(99, 54, 74, 1) 100%)',
        '#DCC7BD',
      ].join(', '),
    } as React.CSSProperties
  }, [mergedConfig?.chatOverlay])

  // 메시지 상태 구독 (스트리밍 포함)
  const { messages } = useRealtimeAgentStore(
    useShallow((state) => ({
      messages: state.messages,
    }))
  )

  // 언어 드롭다운 열림/닫힘 상태
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false)

  // 대화 내역 모달 열림/닫힘 상태
  const [isChatHistoryOpen, setIsChatHistoryOpen] = useState(false)

  // 대화 내역 모달 열기 핸들러
  const handleOpenChatHistory = useCallback(() => {
    resetIdleTimer()
    useToastStore.setState({ toasts: [] })
    setIsChatHistoryOpen(true)
  }, [resetIdleTimer])

  // 대화 내역 모달 닫기 핸들러
  const handleCloseChatHistory = useCallback(() => {
    resetIdleTimer()
    setIsChatHistoryOpen(false)
  }, [resetIdleTimer])

  // 세션 리셋 시 모달 자동 닫기
  useEffect(() => {
    if (showCoverModal && isChatHistoryOpen) {
      setIsChatHistoryOpen(false)
    }
  }, [showCoverModal, isChatHistoryOpen])

  // DB config에서 커버 비디오 URL 추출 (없으면 기본값 사용)
  const coverVideoUrl = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const config = (agentData as any)?.config
    const pageStyle = config?.page_style
    return pageStyle?.consent_modal?.video_url
      || pageStyle?.kiosk_config?.cover_video_url
      || '/webm/cover.webm'
  }, [agentData])

  // 3D 아바타 렌더링 조건 (데이터 준비 완료 시 렌더링 시작)
  // ThreeCanvas가 렌더링되어야 isAvatarReady가 true가 됨
  const canRenderCanvas = agentData && (avatarType !== 'custom' || isCustomAvatarLoaded)

  return (
    <main
      className="w-full h-full relative overflow-hidden"
      onClick={handleScreenTouch}
      style={backgroundStyle}
    >
      {/* 커버 모달 (동영상 배경) - 전체 화면 커버 (9:16 컨테이너 밖) */}
      <KioskCoverModal
        isOpen={showCoverModal}
        isLoading={unifiedLoadingState !== 'ready'}
        loadingMessage={loadingMessage}
        videoUrl={coverVideoUrl}
        title={`민원 관련 궁금한 점,\nAI가 바로 답해 드려요!`}
        buttonText="시작하기"
        onStart={handleStart}
      />

      {/* 3D 아바타 캔버스 - 전체 화면 커버 (9:16 컨테이너 밖) */}
      {canRenderCanvas && (
        <ThreeCanvas
          key={`kiosk-canvas-${agentId}-${customAvatar?.id || 'default'}`}
          className="z-[1] absolute inset-0 !h-full !w-full"
          avatarType={avatarType}
          displayMode="kiosk"
        />
      )}

      {/* 립싱크 튜닝 패널 (?tuning=1 로 표시) */}
      <LipsyncTuningPanel />

      {/* 9:16 컨테이너 - UI 요소들만 (배경 위에서 키오스크 비율 유지, Container Query 기준) */}
      <div
        className="relative h-full mx-auto overflow-hidden"
        lang={selectedLanguage === 'zh' ? 'zh-Hans' : selectedLanguage}
        style={{
          width: 'min(100%, calc(100vh * 9 / 16))',
          containerType: 'size',
        }}
      >

      {/* 헤더 (로고 + 타이틀) - 독립 배치 */}
      {!showCoverModal && (
        <KioskHeader
          onLogoClick={handleLogoReset}
          selectedLanguage={selectedLanguage}
        />
      )}

      {/* 언어 드롭다운 (열린 상태) - Figma 디자인 */}
      {!showCoverModal && isLanguageDropdownOpen && (
        <KioskLanguageDropdown
          selectedLanguage={selectedLanguage}
          onSelect={(lang) => {
            setSelectedLanguage(lang)
            changeLanguage(lang)
            setIsLanguageDropdownOpen(false)
          }}
          onClose={() => setIsLanguageDropdownOpen(false)}
        />
      )}

      {/* 인디케이터 (Listening/Loading/Speaking) - 상태 버튼만 표시 */}
      {!showCoverModal && indicatorState !== 'idle' && (
        <KioskIndicator
          state={indicatorState}
          onScreenTouch={handleScreenTouch}
          pitch={pitch}
          selectedLanguage={selectedLanguage}
        />
      )}

      {/* 유휴 타이머 (경고 표시) - 10초부터 카운트다운 */}
      {isTimerVisible && (
        <KioskIdleTimer
          remainingSeconds={remainingSeconds}
          warningSeconds={10}
          onTouch={resetIdleTimer}
        />
      )}

      {/* 대화 기록 버튼 - 좌측 하단 (항상 표시, 대화 없으면 dimmed) */}
      {!showCoverModal && (
        <KioskChatHistoryButton
          onClick={isChatHistoryOpen ? handleCloseChatHistory : handleOpenChatHistory}
          hasMessages={messages.length > 0}
          selectedLanguage={selectedLanguage}
          isChatHistoryOpen={isChatHistoryOpen}
        />
      )}

      {/* 언어 버튼 / 취소 버튼 - 우측 하단 (Figma: right=130, y=3420, 대화기록 버튼과 대칭) */}
      {!showCoverModal && (
        isLanguageDropdownOpen ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setIsLanguageDropdownOpen(false)
            }}
            className="absolute z-[301] flex items-center justify-center pointer-events-auto active:scale-95 transition-all font-pretendard font-bold text-white"
            style={{
              right: `${(130 / 2160) * 100}%`,
              bottom: `${((3840 - 3420 - 200) / 3840) * 100}%`,
              padding: `${(48 / 3840) * 100}cqh ${(60 / 2160) * 100}cqw`,
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              border: `${(10 / 2160) * 100}cqw solid rgba(255, 255, 255, 0.8)`,
              borderRadius: '999px',
              fontSize: `${(70 / 2160) * 100}cqw`,
              letterSpacing: '0.04em',
              whiteSpace: 'nowrap',
            }}
          >
            {CANCEL_TEXT[selectedLanguage]}
          </button>
        ) : (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setIsLanguageDropdownOpen(true)
            }}
            className="absolute z-50 flex items-center justify-center pointer-events-auto active:scale-95 transition-all font-pretendard font-semibold text-white"
            style={{
              right: `${(130 / 2160) * 100}%`,
              bottom: `${((3840 - 3420 - 200) / 3840) * 100}%`,
              padding: `${(48 / 3840) * 100}cqh ${(60 / 2160) * 100}cqw`,
              gap: `${(20 / 2160) * 100}cqw`,
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              border: `${(10 / 2160) * 100}cqw solid rgba(255, 255, 255, 0.2)`,
              borderRadius: '999px',
              backdropFilter: 'blur(111px)',
              fontSize: `${(72 / 2160) * 100}cqw`,
              letterSpacing: '0.04em',
              whiteSpace: 'nowrap' as const,
            }}
          >
            {/* Globe 아이콘 (tabler:world) - Figma: 100x100 */}

            <svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100" fill="none" style={{
              width: `${(100 / 2160) * 100}cqw`,
              height: `${(100 / 2160) * 100}cqw`,
              minWidth: '20px',
              minHeight: '20px',
              flexShrink: 0,
            }}>
              <path d="M15 37.5H85M15 62.5H85M12.5 50C12.5 54.9246 13.47 59.8009 15.3545 64.3506C17.2391 68.9003 20.0013 73.0343 23.4835 76.5165C26.9657 79.9987 31.0997 82.7609 35.6494 84.6455C40.1991 86.53 45.0754 87.5 50 87.5C54.9246 87.5 59.8009 86.53 64.3506 84.6455C68.9003 82.7609 73.0343 79.9987 76.5165 76.5165C79.9987 73.0343 82.7609 68.9003 84.6455 64.3506C86.53 59.8009 87.5 54.9246 87.5 50C87.5 40.0544 83.5491 30.5161 76.5165 23.4835C69.4839 16.4509 59.9456 12.5 50 12.5C40.0544 12.5 30.5161 16.4509 23.4835 23.4835C16.4509 30.5161 12.5 40.0544 12.5 50Z" stroke="white" stroke-width="6.6625" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M47.9283 12.5C40.9089 23.7484 37.1875 36.7411 37.1875 50C37.1875 63.2589 40.9089 76.2516 47.9283 87.5M52.095 12.5C59.1144 23.7484 62.8358 36.7411 62.8358 50C62.8358 63.2589 59.1144 76.2516 52.095 87.5" stroke="white" stroke-width="6.6625" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            {KIOSK_LANGUAGES.find(l => l.code === selectedLanguage)?.name || '한국어'}
          </button>
        )
      )}

      {/* 대화 종료 버튼 - 하단 가운데 (Figma: x=878, y=3470, 아이콘+텍스트) */}
      {!showCoverModal && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            handleLogoReset()
          }}
          className="absolute z-50 flex items-center pointer-events-auto active:scale-95 transition-all font-pretendard font-semibold text-white"
          style={{
            left: '50%',
            transform: 'translateX(-50%)',
            bottom: `${((3840 - 3470 - 100) / 3840) * 100}%`,
            gap: `${(20 / 2160) * 100}cqw`,
            fontSize: `${(72 / 2160) * 100}cqw`,
            whiteSpace: 'nowrap',
          }}
        >
          {/* 대화 종료 아이콘 (Figma: 100x100, circle + X, stroke white 7px) */}
          <img
            src="/svg/end-call-icon.svg"
            alt=""
            style={{
              width: `${(100 / 2160) * 100}cqw`,
              height: `${(100 / 2160) * 100}cqw`,
              minWidth: '20px',
              minHeight: '20px',
              flexShrink: 0,
            }}
          />
          {END_CHAT_BUTTON_TEXT[selectedLanguage]}
        </button>
      )}

      {/* AI 면책 안내 문구 - 최하단 (Figma: x=501, y=3668, Pretendard 600, 48px, rgba(255,255,255,0.5)) */}
      {!showCoverModal && (
        <p
          className="absolute z-50 text-center font-pretendard font-semibold pointer-events-none"
          style={{
            left: '50%',
            transform: 'translateX(-50%)',
            bottom: `${((3840 - 3668 - 140) / 3840) * 100}%`,
            fontSize: `${(48 / 2160) * 100}cqw`,
            lineHeight: '2.917em',
            color: 'rgba(255, 255, 255, 0.5)',
            whiteSpace: 'nowrap',
          }}
        >
          {AI_DISCLAIMER_TEXT[selectedLanguage]}
        </p>
      )}

      {/* 대화 내역 모달 */}
      <KioskChatHistoryModal
        isOpen={isChatHistoryOpen}
        onClose={handleCloseChatHistory}
        messages={messages}
        avatarImageUrl={getAvatarProfileImage(avatarType)}
        selectedLanguage={selectedLanguage}
      />

      {/* Kiosk Toast Container (Figma Bubble 스타일) */}
      <KioskToastContainer />

      </div>
    </main>
  )
}

export default KioskAvatarPage
